import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { X, Search, Settings, Lock, Power } from 'lucide-react';
import { ROLE_DEFINITIONS } from '../CockpitDashboardBackground';

interface CockpitStartMenuProps {
  theme: 'dark' | 'light' | 'purple';
  activeRoleKey: string;
  onClose: () => void;
  onOpenApp: (appType: 'documents' | 'planilhas' | 'apresentacoes') => void;
  onOpenFolder: () => void;
  onLogout: () => void;
  onOpenTransitions: () => void;
}

const PINNED_APPS_LIST = [
  { id: 'explorer', name: 'Explorador', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H10l2 2h6.5A2.5 2.5 0 0 1 21 9.5v7A2.5 2.5 0 0 1 18.5 19h-13A2.5 2.5 0 0 1 3 16.5z" />
    </svg>
  )},
  { id: 'projects', name: 'Projetos', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 8h8M8 12h5M8 16h7" />
    </svg>
  )},
  { id: 'documents', name: 'Documentos', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2h8l4 4v16H6z" />
      <path d="M14 2v5h5" />
      <path d="M9 12h6M9 16h4" />
    </svg>
  )},
  { id: 'simulation', name: 'Simulação', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16v16H4z" />
      <path d="M4 9h16M9 9v11" />
    </svg>
  )},
  { id: 'planilhas', name: 'Planilhas', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2h8l4 4v16H6z" />
      <path d="M9 11h6M9 15h6M12 9v10" />
    </svg>
  )},
  { id: 'apresentacoes', name: 'Apresentações', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="16" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  )},
  { id: 'chat', name: 'Bate-papo', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
    </svg>
  )},
  { id: 'terminal', name: 'Terminal', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="m7 9 3 3-3 3M13 15h4" />
    </svg>
  )},
  { id: 'security', name: 'Segurança', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3 4 6v5c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  )},
  { id: 'ai', name: 'IA', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="5" width="14" height="14" rx="4" />
      <path d="M9 9h.01M15 9h.01M9 15h6" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  )},
  { id: 'settings', name: 'Configurações', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.5-2.4 1A7 7 0 0 0 14.5 5L14 2.5h-4L9.5 5a7 7 0 0 0-2 1.8l-2.4-1-2 3.5 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.5 2.4-1A7 7 0 0 0 9.5 19l.5 2.5h4l.5-2.5a7 7 0 0 0 2-1.8l2.4 1 2-3.5-2-1.5c.1-.4.1-.8.1-1.2z" />
    </svg>
  )},
  { id: 'transitions', name: 'Transições', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M21 9H3M21 15H3M12 3v18" />
    </svg>
  )}
];

const RECENT_FILES_LIST = [
  { title: 'Aluno Conectado — Central de Simulação', desc: 'Projeto · 18 arquivos modificados', date: 'Agora', icon: '📝' },
  { title: 'Proposta Prefeitura — versão final.docx', desc: 'Documento · 42 páginas', date: '12 min', icon: '📄' },
  { title: 'Apresentação Escola Segura.pptx', desc: 'Apresentação · 28 slides', date: 'Hoje', icon: '💻' }
];

export const CockpitStartMenu: React.FC<CockpitStartMenuProps> = ({
  theme,
  activeRoleKey,
  onClose,
  onOpenApp,
  onOpenFolder,
  onLogout,
  onOpenTransitions
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const role = ROLE_DEFINITIONS[activeRoleKey.toLowerCase() as keyof typeof ROLE_DEFINITIONS] || ROLE_DEFINITIONS.administrador;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        const startBtn = document.querySelector('[title="Menu Iniciar"], .start-btn-trigger');
        if (startBtn && startBtn.contains(event.target as Node)) {
          return;
        }
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const startMenuThemeClass = {
    purple: 'bg-[#120f26]/95 border-[#4c1d95]/40 text-white shadow-black/80',
    dark: 'bg-[#0f0f15]/95 border-white/10 text-white shadow-black/85',
    light: 'bg-[#ffffff]/95 border-[#cbd5e1] text-[#0f172a] shadow-slate-200/50',
  }[theme];

  const handleAppLaunch = (appId: string) => {
    onClose();
    if (appId === 'explorer') {
      onOpenFolder();
    } else if (appId === 'documents') {
      onOpenApp('documents');
    } else if (appId === 'planilhas') {
      onOpenApp('planilhas');
    } else if (appId === 'apresentacoes') {
      onOpenApp('apresentacoes');
    } else if (appId === 'transitions') {
      onOpenTransitions();
    } else {
      toast.info(`Inicializando ${appId}...`);
    }
  };

  const showOnlyOffice = import.meta.env.VITE_ENABLE_ONLYOFFICE === 'true';
  const appsList = showOnlyOffice
    ? PINNED_APPS_LIST
    : PINNED_APPS_LIST.filter(app => !['documents', 'planilhas', 'apresentacoes'].includes(app.id));

  const filteredApps = appsList.filter(app => 
    app.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <section 
      ref={menuRef}
      className={`fixed bottom-[88px] left-4 w-[510px] max-w-full rounded-[24px] border p-6 z-[10000] backdrop-blur-[30px] shadow-2xl flex flex-col gap-4 animate-slide-up select-none ${startMenuThemeClass}`}
    >
      {/* Header Contexto do Perfil */}
      <div className="flex items-center justify-between border-b border-white/10 pb-3.5">
        <div className="flex items-center gap-3">
          <div 
            className="w-[42px] h-[42px] rounded-[13px] flex items-center justify-center border border-white/20 shadow-lg text-white"
            style={{
              background: `linear-gradient(135deg, ${role.primary}e6 0%, ${role.secondary}80 100%)`,
            }}
          >
            {role.icon}
          </div>
          <div className="flex flex-col text-left">
            <strong className="text-[12px] font-black text-white tracking-wide">{role.name}</strong>
            <span className="text-[8px] font-extrabold text-white/50 uppercase tracking-widest mt-0.5">{role.eyebrow} · ALUNO CONECTADO</span>
          </div>
        </div>
        <span className="h-6 px-3.5 rounded-full border border-[#58e19d]/25 bg-[#58e19d]/10 flex items-center text-[8px] font-black text-[#58e19d] uppercase tracking-wider">
          • Ambiente ativo
        </span>
      </div>

      {/* Barra de Pesquisa */}
      <label className="flex items-center gap-3 px-4 py-3 bg-black/35 border border-white/5 rounded-2xl cursor-text focus-within:border-white/20 transition-all shadow-inner">
        <Search size={14} className="text-white/40" />
        <input 
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Pesquisar aplicativos, projetos e documentos..."
          className="w-full bg-transparent border-none focus:outline-none text-[11px] text-white placeholder-white/30"
        />
      </label>

      {/* Aplicativos Fixados */}
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center px-1">
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-white/50">Aplicativos fixados</span>
          <button className="text-[9px] font-extrabold uppercase tracking-wider text-white/40 hover:text-white bg-transparent border-none cursor-pointer">
            Todos os aplicativos
          </button>
        </div>

        <div className="grid grid-cols-6 gap-3">
          {filteredApps.map((app) => (
            <button
              key={app.id}
              onClick={() => handleAppLaunch(app.id)}
              className="flex flex-col items-center justify-center p-2 rounded-2xl hover:bg-white/5 transition-all active:scale-95 border-none bg-transparent cursor-pointer group"
            >
              <div 
                className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#a6adc8] group-hover:text-white group-hover:scale-105 transition-all shadow-md"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.01) 100%), rgba(20,20,35,0.4)',
                }}
              >
                {app.icon}
              </div>
              <span className="text-[8.5px] font-bold text-[#a6adc8] group-hover:text-white mt-1.5 text-center truncate w-full">
                {app.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Projetos e Arquivos Recentes */}
      <div className="flex flex-col gap-2.5">
        <div className="flex justify-between items-center px-1">
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-white/50">Projetos e arquivos recentes</span>
          <span className="text-[8px] font-extrabold uppercase tracking-wider text-white/30">Continuar de onde parou</span>
        </div>

        <div className="flex flex-col gap-2">
          {RECENT_FILES_LIST.map((file, idx) => (
            <div 
              key={idx}
              onClick={() => toast.info(`Abrindo recente: ${file.title}`)}
              className="flex items-center justify-between p-2.5 rounded-2xl hover:bg-white/5 border border-transparent hover:border-white/5 cursor-pointer transition-all active:scale-[0.99] group text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[15px] shadow-sm">
                  {file.icon}
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] font-bold text-white group-hover:text-[#a78bfa] transition-colors truncate">{file.title}</span>
                  <span className="text-[8px] text-white/40 font-semibold mt-0.5 truncate">{file.desc}</span>
                </div>
              </div>
              <span className="text-[8px] font-black text-white/40 tracking-wider uppercase pr-2">{file.date}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer / Perfil do Usuário e Opções de Energia */}
      <div className="flex items-center justify-between border-t border-white/10 pt-4 mt-1">
        {/* User Card */}
        <div className="flex items-center gap-3">
          <div className="relative w-9 h-9 rounded-full bg-gradient-to-tr from-[#9b6cff] to-[#3de5ff] p-[1.5px] shadow-md flex-shrink-0">
            <div className="w-full h-full rounded-full bg-[#12121e] flex items-center justify-center text-[10px] font-black text-white">
              EM
            </div>
            <span className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full bg-[#58e19d] border-2 border-[#12121e]" />
          </div>
          <div className="flex flex-col text-left">
            <span className="text-[10px] font-black text-white">Euler Magalhães</span>
            <span className="text-[8px] text-white/45 font-bold mt-0.5">Administrador · ambiente seguro</span>
          </div>
        </div>

        {/* Energy Action Buttons */}
        <div className="flex items-center gap-1.5">
          <button 
            onClick={() => toast.info('Abrir Configurações')}
            className="w-8 h-8 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/10 text-white/50 hover:text-white transition-all flex items-center justify-center cursor-pointer active:scale-90"
            title="Configurações"
          >
            <Settings size={14} />
          </button>
          <button 
            onClick={() => toast.info('Bloquear Tela')}
            className="w-8 h-8 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/10 text-white/50 hover:text-white transition-all flex items-center justify-center cursor-pointer active:scale-90"
            title="Bloquear Tela"
          >
            <Lock size={14} />
          </button>
          <button 
            onClick={onLogout}
            className="w-8 h-8 rounded-xl bg-[#f38ba8]/15 border border-[#f38ba8]/20 hover:bg-[#f38ba8]/25 text-[#f38ba8] transition-all flex items-center justify-center cursor-pointer active:scale-90"
            title="Desligar / Encerrar Simulação"
          >
            <Power size={14} />
          </button>
        </div>
      </div>

    </section>
  );
};
